/**
 * Marketing Leads Overview — fresh redesign.
 *
 * Visual language change vs. the prior version:
 *   - Cards swapped for "panels" with soft radial gradients + thin
 *     dotted borders (the old white-card-on-grey pattern got
 *     replaced because every section looked identical).
 *   - Numbers right-anchored on coloured progress rails so the eye
 *     ranks them by length rather than reading every label.
 *   - Funnel switched from a tapering polygon to a stair-step pill
 *     rail that survives 0% stages without disappearing.
 *   - Donut replaced with a horizontal "Channel Mix" rail — easier
 *     to scan, no center-text awkwardness.
 *
 * Naming refresh — real-estate-native vocabulary that mirrors how
 * agents and sales managers actually talk about their book:
 *
 *   Total Leads        →  Buyer Pipeline
 *   Active / Inactive  →  Active / Closed
 *   All / Unassigned / Deleted / Duplicate
 *                      →  All Leads / Unassigned / Closed / Duplicates
 *   Open — Not Connected → Pending Call
 *   Overdue            →  Overdue Follow-up
 *   Not a Sales Lead   →  Disqualified
 *   Cold / Warm / New / Broker / New Lead
 *                      →  Cold / Warm / New Inquiry / Broker / Walk-in
 *   Leads vs Source    →  Lead Sources
 *   Lead Funnel        →  Sales Pipeline
 *
 * Funnel stages get the standard RE progression:
 *   Pool → Pursuit → Pitch → Reserved → Sealed
 *   becomes
 *   Lead → Qualified → Site Visit → Booking → Closed
 *
 * The data shape is unchanged — only the presentation + copy. The
 * backend payload still arrives keyed by the original snake_case
 * keys, and the renames happen via a small label-map below.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Sparkles, ArrowRight,
  Activity, Layers, GitBranch, MoveRight, Users,
} from 'lucide-react';
import HelpInfo from '@/components/HelpInfo';
import InfoBadge from '@/components/InfoBadge';

// Lifecycle-stage → pill colour. Matches the tones on MyMarketingDashboard
// so the Recent Leads chips look identical wherever they're rendered.
const STAGE_STYLE = {
  new:              'bg-slate-100 text-slate-600 border-slate-200',
  enquiry:          'bg-slate-100 text-slate-600 border-slate-200',
  qualified:        'bg-blue-50 text-blue-700 border-blue-200',
  site_visit:       'bg-amber-50 text-amber-700 border-amber-200',
  'site visit':     'bg-amber-50 text-amber-700 border-amber-200',
  negotiation:      'bg-violet-50 text-violet-700 border-violet-200',
  booking:          'bg-sky-50 text-sky-700 border-sky-200',
  booking_initiated:'bg-sky-50 text-sky-700 border-sky-200',
  closed:           'bg-emerald-50 text-emerald-700 border-emerald-200',
  won:              'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost:             'bg-rose-50 text-rose-700 border-rose-200',
};
const stageStyle = (s) => STAGE_STYLE[(s || 'new').toLowerCase()] || STAGE_STYLE.new;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return '0';
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString();
};

const fmtAED = (n) => {
  if (!n || Number.isNaN(Number(n))) return 'AED 0';
  const v = Number(n);
  if (v >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `AED ${(v / 1_000).toFixed(1)}K`;
  return `AED ${v.toLocaleString('en-AE')}`;
};

// Momentum chip used inside the status rail rows.
const Momentum = ({ pct }) => {
  if (pct == null) return null;
  const flat = pct === 0;
  const up = pct > 0;
  const cls = flat
    ? 'text-slate-400'
    : up
      ? 'text-emerald-500'
      : 'text-rose-500';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`}>
      {flat ? '·' : up ? '+' : ''}{flat ? '' : `${pct}%`}
      {!flat && (up ? <TrendingUp size={10} /> : <TrendingDown size={10} />)}
    </span>
  );
};

// ---- Lead Pool — left "hero" panel --------------------------------------
// Big number on top, sparkline below, then a stacked Live / Resting
// readout with proportional bars instead of two text pills. The whole
// thing sits inside a soft radial-gradient panel so it reads as the
// "headline" without needing a border.
const Sparkline = ({ data = [] }) => {
  const points = data.length > 1 ? data : [0, 0];
  const width = 240, height = 64;
  const max = Math.max(1, ...points);
  const step = width / (points.length - 1);
  const y = (v) => height - (v / max) * (height - 8) - 4;
  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step},${y(v)}`).join(' ');
  const area = `${line} L ${(points.length - 1) * step},${height} L 0,${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="pool-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="#7c3aed" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#pool-grad)" />
      <path d={line} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" />
      <circle cx={(points.length - 1) * step} cy={y(points[points.length - 1])} r="9" fill="#7c3aed" fillOpacity="0.18" />
      <circle cx={(points.length - 1) * step} cy={y(points[points.length - 1])} r="4" fill="#7c3aed" />
    </svg>
  );
};

const LeadPool = ({ data }) => {
  const total = data?.total_leads || 0;
  const live  = data?.active || 0;
  const rest  = data?.inactive || 0;
  const livePct = total ? Math.round(100 * live / total) : 0;
  const restPct = 100 - livePct;
  return (
    <div className="relative rounded-2xl p-6 overflow-hidden
                    bg-gradient-to-br from-violet-50 via-white to-blue-50/40
                    border border-violet-100/70">
      {/* Decorative blurred blob — defines the "panel feels alive"
          aesthetic for the new dashboard. */}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-violet-600 font-bold">
            Buyer Pipeline
          </span>
          <HelpInfo
            align="inline"
            title="Buyer Pipeline"
            steps={[
              'The total number of buyer inquiries in your book — active and closed.',
              'The chart below shows daily inquiries over the last 14 days; the dot marks today.',
              <><span className="font-medium text-emerald-700">Active</span> = buyers still in pursuit. <span className="font-medium text-slate-700">Closed</span> = deal won, lost, or archived.</>,
            ]}
            tip="A flat chart with rising Closed count usually means the team is wrapping up old deals without filling the top of the pipeline."
          />
        </div>
        <p className="text-[44px] leading-none font-bold text-slate-900 mt-2 tracking-tighter">{fmt(total)}</p>
        <div className="mt-4 -mx-1">
          <Sparkline data={data?.sparkline || []} />
        </div>
        {/* Active / Closed stacked rail */}
        <div className="mt-4 space-y-2.5">
          <div>
            <div className="flex items-center justify-between text-[11px] font-medium">
              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Active
              </span>
              <span className="text-slate-700">{fmt(live)} <span className="text-slate-400">· {livePct}%</span></span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${livePct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-[11px] font-medium">
              <span className="inline-flex items-center gap-1.5 text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                Closed
              </span>
              <span className="text-slate-700">{fmt(rest)} <span className="text-slate-400">· {restPct}%</span></span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
              <div className="h-full rounded-full bg-slate-400 transition-all" style={{ width: `${restPct}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Status rail — right "list" panel -----------------------------------
// Old version was 8 boxes in a grid. New version is a single rail of rows
// — each row shows the count as a horizontal coloured bar so the eye can
// rank statuses by bar length. Much easier to scan than 8 identical pills.
const STATUS_RENAMES = {
  open_not_connected: { label: 'Pending Call',       tone: 'emerald' },
  overdue:           { label: 'Overdue Follow-up',   tone: 'red'     },
  not_a_sales_lead:  { label: 'Disqualified',        tone: 'slate'   },
  cold:              { label: 'Cold',                tone: 'orange'  },
  warm:              { label: 'Warm',                tone: 'rose'    },
  new:               { label: 'New Inquiry',         tone: 'blue'    },
  broker:            { label: 'Broker',              tone: 'sky'     },
  new_lead:          { label: 'Walk-in',             tone: 'violet'  },
};

const TONE = {
  emerald: { bar: 'bg-emerald-400', dot: 'bg-emerald-500', txt: 'text-emerald-700' },
  red:     { bar: 'bg-red-400',     dot: 'bg-red-500',     txt: 'text-red-700'     },
  slate:   { bar: 'bg-slate-400',   dot: 'bg-slate-500',   txt: 'text-slate-700'   },
  orange:  { bar: 'bg-orange-400',  dot: 'bg-orange-500',  txt: 'text-orange-700'  },
  rose:    { bar: 'bg-rose-400',    dot: 'bg-rose-500',    txt: 'text-rose-700'    },
  blue:    { bar: 'bg-blue-400',    dot: 'bg-blue-500',    txt: 'text-blue-700'    },
  sky:     { bar: 'bg-sky-400',     dot: 'bg-sky-500',     txt: 'text-sky-700'     },
  violet:  { bar: 'bg-violet-400',  dot: 'bg-violet-500',  txt: 'text-violet-700'  },
};

const TAB_RENAMES = {
  all:        'All Leads',
  unassigned: 'Unassigned',
  deleted:    'Closed',
  duplicate:  'Duplicates',
};

// Friendly empty-state messages shown when the active tab has zero
// records overall — keeps the rail from rendering 8 silent zeros that
// could look like a broken fetch.
const TAB_EMPTY_STATE = {
  all:        { title: 'No leads yet',           note: 'Once inquiries start arriving, the status rail fills in here.' },
  unassigned: { title: 'No unassigned leads',    note: 'Every lead has an owner — your team is on top of the queue.' },
  deleted:    { title: 'No closed leads yet',    note: 'Closed records will appear here once leads move to a terminal status.' },
  duplicate:  { title: 'No duplicates detected', note: 'Leads matching an existing record by phone, email, or Emirates ID will surface here.' },
};

const StatusRail = ({ statuses = [], tabs = {}, tab, setTab, refreshing = false }) => {
  // Bar widths are relative to the max count so the longest row hits
  // 100% — gives the eye a clean ranking without compressing tiny
  // values into invisible slivers.
  const maxCount = Math.max(1, ...statuses.map((s) => s.count || 0));
  // Total leads currently in the active-tab scope. When this is 0 we
  // render an empty-state message instead of the eight zero-bars,
  // which used to make the rail look broken on an empty Duplicates /
  // Closed tab.
  const tabTotal = Number(tabs[tab] || 0);
  const allZero = statuses.every((s) => !s.count);
  const showEmpty = tabTotal === 0 && allZero;
  const empty = TAB_EMPTY_STATE[tab] || TAB_EMPTY_STATE.all;
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 p-5
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
        <div className="flex items-center gap-0.5">
          {Object.entries(TAB_RENAMES).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`relative px-3.5 py-1.5 text-sm font-medium transition rounded-lg ${
                tab === key
                  ? 'text-violet-700 bg-violet-50'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              {label}
              {/* Always show the count, even when 0 — "Duplicates 0"
                  reads as "zero duplicates" instead of looking like
                  missing data. */}
              {tabs[key] != null && (
                <span className={`ml-1.5 text-[10px] ${tab === key ? 'text-violet-500' : 'text-slate-400'}`}>
                  {fmt(tabs[key])}
                </span>
              )}
            </button>
          ))}
        </div>
        <HelpInfo
          align="inline"
          title="Lead Status Breakdown"
          steps={[
            'Each row shows how many leads are currently sitting in that status.',
            'The bar length is relative to the largest row — easy visual rank.',
            'The momentum number on the right compares against the prior identical window (e.g. last 7 days vs the 7 days before that).',
          ]}
          tip="A high Overdue Follow-up count paired with red momentum is the strongest coaching signal of the week — those buyers are slipping."
        />
      </div>
      {showEmpty ? (
        <div className="mt-6 mb-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-700">{empty.title}</p>
          <p className="mt-1 text-xs text-slate-500">{empty.note}</p>
        </div>
      ) : (
        <div
          className={`mt-3 space-y-2 transition-opacity duration-150 ${
            // While a tab-change refetch is in flight, dim the rail
            // slightly so the user gets immediate "we heard you" feedback
            // even before the new numbers arrive. The actual fetch is
            // typically < 200ms once the backend ran the parallel-gather
            // optimisation, but this keeps the rail feeling responsive
            // on slow connections / cold DB.
            refreshing ? 'opacity-40 pointer-events-none' : 'opacity-100'
          }`}
        >
          {statuses.map((s) => {
            const meta = STATUS_RENAMES[s.key] || { label: s.label, tone: 'slate' };
            const t = TONE[meta.tone];
            const pct = Math.round(100 * (s.count || 0) / maxCount);
            return (
              <div key={s.key} className="group flex items-center gap-3 py-1.5 rounded-lg
                                           hover:bg-slate-50 px-2 -mx-2 transition cursor-pointer">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.dot}`} />
                <span className={`text-[12px] font-medium ${t.txt} w-[140px] shrink-0`}>
                  {meta.label}
                </span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${t.bar} transition-all group-hover:brightness-110`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-slate-900 tabular-nums w-10 text-right">
                  {fmt(s.count)}
                </span>
                <div className="w-14 text-right">
                  <Momentum pct={s.delta_pct} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- Channel Mix — replaces "Leads vs Source" donut --------------------
// Horizontal stacked bar at the top showing the share of each channel,
// then a colour-keyed list with channel name + lead count + revenue.
// Reads faster than a donut and works on any width.
const CHANNEL_COLORS = ['#7c3aed', '#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

const ChannelMix = ({ sources = [], received = 0, total = 0, days = 7, recentLeads = [], onOpenLead }) => {
  const denom = sources.reduce((s, x) => s + (x.leads || 0), 0) || 1;
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 p-5 h-full
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-2 flex-wrap">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
            <Layers size={16} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center">
              Lead Sources
              <InfoBadge text="Breakdown of where leads came from in the selected window — portal listings (Bayut / Property Finder), paid ads (Meta / Google), referrals, walk-ins, and direct inquiries. Use this with the funnel below to see which channel actually converts." />
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Where the {fmt(received)} new inquiries from the last {days} days came from
            </p>
          </div>
          {/* HelpInfo popover removed — the inline InfoBadge next to
              the title now carries the same explanation, so the
              second (i) at the right was redundant. */}
        </div>
        <p className="text-[11px] text-slate-400 whitespace-nowrap">
          of {fmt(total)} total
        </p>
      </div>

      {/* Stacked bar */}
      {sources.length === 0 ? (
        <div className="text-center py-10 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
          <p className="text-sm font-medium text-slate-700">No sources firing yet</p>
          <p className="text-xs text-slate-500 mt-1">Portal listings, UTM-tagged campaigns, and walk-in entries fill this view.</p>
        </div>
      ) : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            {sources.map((s, i) => {
              const pct = ((s.leads || 0) / denom) * 100;
              return (
                <div
                  key={s.key || i}
                  className="transition hover:brightness-110"
                  style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length], width: `${pct}%` }}
                  title={`${s.name}: ${s.leads} leads`}
                />
              );
            })}
          </div>
          <ul className="mt-4 space-y-2.5">
            {sources.map((s, i) => {
              const pct = ((s.leads || 0) / denom) * 100;
              return (
                <li key={s.key || i} className="flex items-center gap-3 group">
                  <span
                    className="w-3 h-3 rounded shrink-0"
                    style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }}
                  />
                  <span className="text-sm font-medium text-slate-800 flex-1 truncate">{s.name}</span>
                  <span className="text-xs text-slate-500 tabular-nums w-12 text-right">{pct.toFixed(1)}%</span>
                  <span className="text-sm font-bold text-slate-900 tabular-nums w-10 text-right">{fmt(s.leads)}</span>
                  <span className="text-xs text-emerald-600 tabular-nums w-20 text-right">
                    {fmtAED(s.revenue)}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Recent Leads — the 10 newest leads in the window. Same row
          shape as the standalone card on My Dashboard so the visual is
          familiar. Rendered inside the Lead Sources panel to give the
          empty state something useful before sources start firing. */}
      <div className="mt-5 pt-4 border-t border-slate-200">
        <div className="flex items-center gap-2 mb-2.5">
          <Users size={14} className="text-violet-500" />
          <h4 className="text-sm font-semibold text-slate-800">Recent Leads</h4>
          <InfoBadge text="The 10 most recently created leads across your book, newest first. Each row shows name, source / project, and the current lifecycle stage. Click any row to open the lead's profile." />
        </div>
        {recentLeads.length === 0 ? (
          <p className="text-xs text-slate-500">No leads yet.</p>
        ) : (
          <div className="space-y-1.5">
            {recentLeads.slice(0, 10).map((l) => (
              <div
                key={l.id}
                onClick={() => onOpenLead && onOpenLead(l.id)}
                className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {l.name || l.email || 'Unnamed'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {(l.source || 'direct')}{l.project ? ` · ${l.project}` : ''}
                  </p>
                </div>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ml-2 shrink-0
                              ${stageStyle(l.lifecycle_stage || l.stage)}`}
                >
                  {(l.lifecycle_stage || l.stage || 'new').replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Closure Journey — replaces "Lead Funnel" --------------------------
// Horizontal stair-step pill rail. Each stage is a rounded pill that
// gets narrower (height-wise) and lighter as we move right. Arrows
// between stages communicate flow. Stages with 0 still render as a
// visible thin pill — readers know the shape of the journey even when
// later stages haven't filled yet.
const STAGE_RENAMES = {
  leads:       'Lead',
  prospect:    'Qualified',
  opportunity: 'Site Visit',
  booked:      'Booking',
  invoiced:    'Closed',
};

const STAGE_TINTS = [
  'from-violet-500 to-violet-600',
  'from-violet-400 to-violet-500',
  'from-blue-400 to-blue-500',
  'from-sky-300 to-sky-400',
  'from-cyan-200 to-cyan-300',
];

const ClosureJourney = ({ stages = [] }) => {
  if (!stages.length) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200/70 p-5 h-full
                      shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
            <GitBranch size={16} />
          </div>
          <h3 className="text-base font-semibold text-slate-900">Sales Pipeline</h3>
        </div>
        <div className="text-center py-12 mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
          <p className="text-sm font-medium text-slate-700">Pipeline is empty</p>
          <p className="text-xs text-slate-500 mt-1">Stages fill as buyers move from inquiry to booking.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 p-5 h-full
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
          <GitBranch size={16} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center">
            Sales Pipeline
            <InfoBadge text="The five-stage funnel buyers move through: Lead (inquiry) → Qualified (budget + intent confirmed) → Site Visit (viewing scheduled / done) → Booking (unit reserved, token paid) → Closed (SPA signed). The % inside each pill is that stage as a share of total Leads." />
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Buyer journey from inquiry to closed deal</p>
        </div>
        {/* HelpInfo popover removed — the inline InfoBadge next to
            the title now carries the same explanation, so the
            second (i) at the right was redundant. */}
      </div>

      {/* Stair-step rail — pills get shorter as we step right. */}
      <div className="flex items-stretch gap-1">
        {stages.map((s, i) => {
          const label = STAGE_RENAMES[s.key] || s.label;
          // Each pill steps from 100% to 60% height so the rail
          // tapers visually without making low-pct pills vanish.
          const hPct = 100 - i * 8;
          return (
            <React.Fragment key={s.key}>
              <div className="flex-1 flex flex-col">
                <div className="border-l-2 border-slate-200 pl-2 mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
                  <p className="text-xl font-bold text-slate-900 tabular-nums">{fmt(s.count)}</p>
                </div>
                <div className="relative flex-1 min-h-[100px] flex items-center">
                  <div
                    style={{ height: `${hPct}%` }}
                    className={`w-full rounded-xl bg-gradient-to-r ${STAGE_TINTS[i]} flex items-center justify-center
                                shadow-sm transition hover:brightness-110`}
                    title={`${label}: ${s.count} (${s.pct}%)`}
                  >
                    <span className="text-white text-sm font-bold drop-shadow-sm">{s.pct}%</span>
                  </div>
                </div>
              </div>
              {i < stages.length - 1 && (
                <div className="flex items-center pt-12 px-0.5">
                  <MoveRight size={14} className="text-slate-300" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// Main component
// ============================================================
export default function MarketingLeadsOverview() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Fetch the tab-scoped overview and the recent-leads slice in
      // parallel — the two endpoints are independent, so we don't want
      // one to block the other on the render path. Recent-leads
      // failure is soft (empty list), so a broken /my/overview doesn't
      // hide the whole panel.
      const [overviewRes, recentRes] = await Promise.all([
        axios.get(`${API}/marketing-dashboard/lead-overview`, {
          // Pass the active tab so the status rail + downstream
          // counts come back scoped to it. The tab counts in the
          // response are still global so the tabs themselves never
          // disappear when one is selected.
          params: { token, days: 7, tab },
        }),
        axios.get(`${API}/marketing/my/overview`, { params: { token } })
          .catch(() => ({ data: { data: { recent_leads: [] } } })),
      ]);
      setData(overviewRes.data?.data || null);
      setRecentLeads(recentRes?.data?.data?.recent_leads || []);
    } catch (e) {
      console.warn('lead-overview:', e?.response?.data?.detail || e);
    } finally {
      setLoading(false);
    }
  }, [token, tab]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Skeleton className="h-72 rounded-2xl lg:col-span-4" />
          <Skeleton className="h-72 rounded-2xl lg:col-span-8" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-72 rounded-2xl" />
          <Skeleton className="h-72 rounded-2xl" />
        </div>
      </div>
    );
  }

  const statuses = data?.statuses || [];
  const sources  = data?.sources  || [];
  const funnel   = data?.funnel   || [];
  const tabs     = data?.tabs     || {};

  return (
    <div className="space-y-4">
      {/* Welcome banner removed — Dashboard.js renders its own at the
          page top so the greeting only shows once. */}

      {/* Row 1 — Lead Pool (left, 4 cols) + Status Rail (right, 8 cols).
          Asymmetric on purpose: the headline number gets its own
          quiet panel while the eight statuses get a long readable rail. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4">
          <LeadPool data={data} />
        </div>
        <div className="lg:col-span-8">
          <StatusRail
            statuses={statuses}
            tabs={tabs}
            tab={tab}
            setTab={setTab}
            // Dim the rail while a tab-change refetch is in flight —
            // ``loading`` is true on first load AND on every refetch,
            // but we only want the dim effect when there's already
            // data on screen (i.e. tab switch, not initial mount).
            refreshing={loading && !!data}
          />
        </div>
      </div>

      {/* Row 2 — Channel Mix (7 cols) + Closure Journey (5 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <ChannelMix
            sources={sources}
            received={data?.received_in_window}
            total={data?.total_leads}
            days={data?.window_days}
            recentLeads={recentLeads}
            onOpenLead={(id) => navigate(`/contacts/${id}`)}
          />
        </div>
        <div className="lg:col-span-5">
          <ClosureJourney stages={funnel} />
        </div>
      </div>
    </div>
  );
}
